// Lector EXIF propio, sin dependencias: solo las tres fechas que usa captureTimestamp().
// Portado del hotfix de producción 1b18433 (2025-12-14), que sustituyó exif-js porque leía el
// fichero entero y fallaba en silencio con algunos JPEG. Aquí se leen como mucho los primeros
// 64 KB, suficientes para el segmento APP1, y cualquier error devuelve {} (EXIF ausente).

const EXIF_HEAD_BYTES = 65536;
const DATE_TAGS = { 0x9003: 'DateTimeOriginal', 0x9004: 'DateTimeDigitized', 0x0132: 'DateTime' };
const EXIF_IFD_POINTER = 0x8769;
const ASCII = 2;

export async function readExifTags(file) {
    try {
        return exifDateTags(new DataView(await readHead(file)));
    } catch {
        return {};
    }
}

// Blob.arrayBuffer() falta en Safari < 14 y en el jsdom de los tests: FileReader como reserva.
function readHead(file) {
    const head = file.slice(0, EXIF_HEAD_BYTES);
    if (typeof head.arrayBuffer === 'function') return head.arrayBuffer();
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(head);
    });
}

// Recorre los segmentos JPEG hasta el APP1 "Exif\0\0" y devuelve las fechas que encuentre.
export function exifDateTags(view) {
    try {
        if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) return {};
        let offset = 2;
        while (offset + 4 <= view.byteLength) {
            if (view.getUint8(offset) !== 0xff) break;
            const marker = view.getUint8(offset + 1);
            const segmentLength = view.getUint16(offset + 2);
            if (marker === 0xe1 && offset + 10 <= view.byteLength
                && view.getUint32(offset + 4) === 0x45786966 && view.getUint16(offset + 8) === 0) {
                return parseTiff(view, offset + 10);
            }
            if (marker === 0xda) break; // Start of scan: ya no hay metadatos detrás.
            offset += 2 + segmentLength;
        }
    } catch {
        // Cabecera truncada o corrupta: sin fechas.
    }
    return {};
}

function parseTiff(view, tiffStart) {
    if (tiffStart + 8 > view.byteLength) return {};
    const littleEndian = view.getUint16(tiffStart) === 0x4949;
    const ifd0 = view.getUint32(tiffStart + 4, littleEndian);
    const tags = {};
    const exifIfd = readIfd(view, tiffStart, ifd0, littleEndian, tags);
    if (exifIfd) readIfd(view, tiffStart, exifIfd, littleEndian, tags);
    return tags;
}

// Lee un IFD, acumula las fechas ASCII en `tags` y devuelve el puntero al Exif IFD si aparece.
function readIfd(view, tiffStart, ifdOffset, littleEndian, tags) {
    const base = tiffStart + ifdOffset;
    if (ifdOffset === 0 || base + 2 > view.byteLength) return 0;
    const entries = view.getUint16(base, littleEndian);
    let exifIfd = 0;
    for (let i = 0; i < entries; i++) {
        const entry = base + 2 + i * 12;
        if (entry + 12 > view.byteLength) break;
        const tag = view.getUint16(entry, littleEndian);
        if (tag === EXIF_IFD_POINTER) {
            exifIfd = view.getUint32(entry + 8, littleEndian);
        } else if (DATE_TAGS[tag] && view.getUint16(entry + 2, littleEndian) === ASCII) {
            const count = view.getUint32(entry + 4, littleEndian);
            // Una fecha EXIF ocupa 20 bytes con su NUL, así que siempre viene por puntero.
            if (count >= 19 && count <= 25) {
                const value = readAscii(view, tiffStart + view.getUint32(entry + 8, littleEndian), 19);
                if (value && tags[DATE_TAGS[tag]] === undefined) tags[DATE_TAGS[tag]] = value;
            }
        }
    }
    return exifIfd;
}

function readAscii(view, offset, length) {
    if (offset < 0 || offset + length > view.byteLength) return null;
    let text = '';
    for (let i = 0; i < length; i++) {
        const code = view.getUint8(offset + i);
        if (code === 0) break;
        text += String.fromCharCode(code);
    }
    return text;
}
